import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeftIcon,
  CloudArrowUpIcon,
  CheckCircleIcon,
  RocketLaunchIcon,
  DocumentDuplicateIcon,
} from '@heroicons/react/24/outline';
import { WorkflowDefinition } from '../../../types/workflow';

interface ToolbarProps {
  workflowName: string;
  workflowDescription: string;
  workflowCategory: string;
  onNameChange: (name: string) => void;
  onDescriptionChange: (description: string) => void;
  onCategoryChange: (category: string) => void;
  onSave: () => void;
  onPublish: () => void;
  onValidate: () => void;
  isDirty: boolean;
  isReadOnly: boolean;
  isNewWorkflow: boolean;
  isSaving: boolean;
  workflow?: WorkflowDefinition | null;
  hasValidationErrors?: boolean;
}

const Toolbar: React.FC<ToolbarProps> = ({
  workflowName,
  workflowDescription,
  workflowCategory,
  onNameChange,
  onDescriptionChange,
  onCategoryChange,
  onSave,
  onPublish,
  onValidate,
  isDirty,
  isReadOnly,
  isNewWorkflow,
  isSaving,
  workflow,
  hasValidationErrors = false,
}) => {
  return (
    <div className="bg-white border-b border-gray-200 px-3 py-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Link
            to="/workflows"
            className="inline-flex items-center text-xs text-gray-500 hover:text-gray-700"
          >
            <ArrowLeftIcon className="mr-1 h-3 w-3" />
            Back
          </Link>
          
          <div className="flex items-center space-x-2">
            {isReadOnly && !isNewWorkflow ? (
              <>
                <h2 className="text-base font-semibold text-gray-900">{workflowName}</h2>
                {workflow?.status && (
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      workflow.status === 'PUBLISHED'
                        ? 'bg-green-100 text-green-800'
                        : workflow.status === 'DRAFT'
                        ? 'bg-gray-100 text-gray-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}
                  >
                    {workflow.status}
                  </span>
                )}
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                  Read-only
                </span>
              </>
            ) : (
              <>
                <input
                  type="text"
                  value={workflowName}
                  onChange={(e) => onNameChange(e.target.value)}
                  placeholder="Workflow Name"
                  className="text-base font-semibold text-gray-900 border-0 border-b border-transparent hover:border-gray-300 focus:border-primary-500 focus:outline-none px-1"
                />
                {isDirty && (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                    Unsaved
                  </span>
                )}
              </>
            )}
          </div>
        </div>

        <div className="flex items-center space-x-2">
          {!isReadOnly && (
            <>
              <select
                value={workflowCategory}
                onChange={(e) => onCategoryChange(e.target.value)}
                className="text-xs rounded border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              >
                <option value="APPROVAL">Approval</option>
                <option value="FULFILLMENT">Fulfillment</option>
                <option value="AUTOMATION">Automation</option>
                <option value="NOTIFICATION">Notification</option>
                <option value="INTEGRATION">Integration</option>
                <option value="ALERT">Alert</option>
                <option value="CUSTOM">Custom</option>
              </select>

              <button
                onClick={onSave}
                disabled={isSaving || (!isDirty && !isNewWorkflow)}
                className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <CloudArrowUpIcon className="mr-1.5 h-3.5 w-3.5" />
                {isSaving ? 'Saving...' : 'Save'}
              </button>

              <button
                onClick={onValidate}
                className={`inline-flex items-center px-3 py-1.5 border text-xs font-medium rounded focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 ${
                  hasValidationErrors 
                    ? 'border-amber-400 bg-amber-50 text-amber-700 hover:bg-amber-100' 
                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                }`}
                title="Check workflow for errors"
              >
                <CheckCircleIcon className={`mr-1.5 h-3.5 w-3.5 ${
                  hasValidationErrors ? 'text-amber-600' : ''
                }`} />
                {hasValidationErrors ? 'Fix Issues' : 'Validate'}
              </button>

              {!isNewWorkflow && workflow?.status === 'DRAFT' && (
                <button
                  onClick={onPublish}
                  className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                  <RocketLaunchIcon className="mr-1.5 h-3.5 w-3.5" />
                  Publish
                </button>
              )}
            </>
          )}

          {workflow?.status === 'PUBLISHED' && (
            <button
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <DocumentDuplicateIcon className="mr-2 h-4 w-4" />
              Clone
            </button>
          )}
        </div>
      </div>

      {!isReadOnly && (
        <div className="mt-2">
          <input
            type="text"
            value={workflowDescription}
            onChange={(e) => onDescriptionChange(e.target.value)}
            placeholder="Add a description for this workflow..."
            className="w-full text-xs text-gray-600 border-0 focus:ring-0 px-0"
          />
        </div>
      )}
    </div>
  );
};

export default Toolbar;